import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { ListFilter } from 'lucide-react';

export type FilterSelectOption = {
  value: string;
  label: string;
};

export type FilterSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: FilterSelectOption[];
  placeholder: string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
};

const ALL_VALUE = '__all__';

export const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder,
  emptyLabel = 'All',
  className = '',
  disabled = false,
}) => {
  // Convert empty string to special ALL_VALUE for internal use
  const internalValue = value === '' ? ALL_VALUE : value;

  const handleValueChange = (newValue: string) => {
    // Convert ALL_VALUE back to empty string
    onValueChange(newValue === ALL_VALUE ? '' : newValue);
  };

  // Find the selected option label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption
    ? `${placeholder}: ${selectedOption.label}`
    : placeholder;

  return (
    <Select
      value={internalValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger
        className={`tw:h-auto tw:w-auto tw:min-w-fit tw:bg-white tw:border tw:border-neutral-200 tw:rounded-lg tw:shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] tw:text-sm tw:font-medium tw:px-4 tw:py-[7.5px] tw:gap-2 tw:cursor-pointer ${className}`}
        disabled={disabled}
      >
        <div className="tw:flex tw:items-center tw:gap-2">
          <ListFilter className="tw:w-[13.25px] tw:h-[13.25px] tw:text-neutral-900 tw:shrink-0" />
          <span className="tw:text-neutral-900 tw:text-sm tw:font-medium tw:tracking-[0.07px] tw:whitespace-nowrap">
            {displayText}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent className="tw:max-h-[400px] tw:overflow-y-auto">
        <SelectItem value={ALL_VALUE}>{emptyLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
